import React from 'react';
import { Phone, Mail, MapPin, MessageCircle, X, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Separator } from './ui/separator';

interface ContactModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ContactModal({ isOpen, onOpenChange }: ContactModalProps) {
  const phoneNumbers = [
    { label: "Main Store", number: "+91-9829050157", wa: "919829050157" }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-2xl p-0 overflow-hidden rounded-[2rem] border-none shadow-2xl flex flex-col max-h-[90vh] [&>button]:text-brand-gold [&>button]:opacity-100 [&>button]:hover:bg-white/10 [&>button]:rounded-full [&>button]:p-2">
        <div className="bg-brand-green p-8 text-white relative">
          <DialogHeader>
            <DialogTitle className="text-3xl font-serif text-brand-gold">Contact Us</DialogTitle>
            <DialogDescription className="text-white/70 text-lg">
              We'd love to hear from you. Visit us or reach out online.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-6 sm:p-8 space-y-8 bg-white flex-1 overflow-y-auto">
          {/* Phone Section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2 text-brand-green font-medium">
              <Phone className="h-5 w-5" />
              <span className="uppercase tracking-widest text-xs">Call or WhatsApp</span>
            </div>
            <div className="flex flex-col gap-4">
              {phoneNumbers.map((item, i) => (
                <div key={i} className="p-5 rounded-2xl border border-border bg-off-white/50 flex flex-col sm:flex-row items-center justify-between group hover:border-brand-green/30 transition-colors">
                  <div className="text-center sm:text-left mb-4 sm:mb-0">
                    <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">{item.label}</p>
                    <p className="text-lg font-bold text-brand-green">{item.number}</p>
                  </div>
                  <div className="flex space-x-3 w-full sm:w-auto">
                    <Button 
                      variant="outline" 
                      size="lg" 
                      className="flex-1 sm:flex-initial rounded-xl border-brand-green text-brand-green hover:bg-brand-green hover:text-white px-6"
                      onClick={() => window.open(`tel:${item.number}`)}
                    >
                      <Phone className="h-4 w-4 mr-2" /> Call
                    </Button>
                    <Button 
                      size="lg" 
                      className="flex-1 sm:flex-initial rounded-xl bg-[#25D366] hover:bg-[#20ba5a] text-white px-6"
                      onClick={() => window.open(`https://wa.me/${item.wa}`)}
                    >
                      <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* Email Section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2 text-brand-green font-medium">
              <Mail className="h-5 w-5" />
              <span className="uppercase tracking-widest text-xs">Email Address</span>
            </div>
            <div className="p-4 rounded-2xl border border-border bg-off-white/50 flex items-center justify-between">
              <p className="text-lg font-medium text-brand-green">info@rpksons.com</p>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-brand-green hover:bg-brand-green/10"
                onClick={() => window.open('mailto:info@rpksons.com')}
              >
                Copy
              </Button>
            </div>
          </div>

          <Separator />

          {/* Location Section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2 text-brand-green font-medium">
              <MapPin className="h-5 w-5" />
              <span className="uppercase tracking-widest text-xs">Our Store in Alwar</span>
            </div>
            <div className="space-y-4">
              <p className="text-muted-foreground leading-relaxed">
                Shop no 148, Jai Complex, Road No. 2, Arya Nagar, Alwar, Rajasthan 301001
              </p>
              <div className="h-64 rounded-2xl overflow-hidden border border-border">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2809.1837591364115!2d76.60585127438613!3d27.56845607626241!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39729990eea0d0c5%3A0x81ff3a43c1b9a45e!2sRam%20Pratap%20Katta%20and%20Sons!5e1!3m2!1sen!2sin!4v1778128726323!5m2!1sen!2sin" 
                  width="100%" 
                  height="100%" 
                  style={{ border: 0 }} 
                  allowFullScreen={true} 
                  loading="lazy" 
                  title="Store Location"
                />
              </div>
              <Button 
                className="w-full bg-brand-green text-white hover:bg-brand-green/90 rounded-xl py-6"
                onClick={() => window.open('https://www.google.com/maps/search/?api=1&query=Ram+Pratap+Katta+and+Sons+Alwar+Rajasthan')}
              >
                Open in Google Maps <ExternalLink className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
